import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Peer from "simple-peer";
import socket from "./signaling/socket";
import SignalingManager from "./signaling/SignalingManager";
import "./GuestRoom.css";

const signaling = new SignalingManager(socket);

// --- ICE servers (STUN/TURN)
const DEFAULT_ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  // local / coturn fallback (adjust host/ports to your setup)
  { urls: "stun:staging.vemeet.online:3478" },
  // TURN (UDP/TCP/TLS) - ensure coturn matches these credentials
  {
    urls: "turn:staging.vemeet.online:3478?transport=udp",
    username: "staginguser",
    credential: "stagingpassword",
  },
  {
    urls: "turn:staging.vemeet.online:3478?transport=tcp",
    username: "staginguser",
    credential: "stagingpassword",
  },
  {
    urls: "turns:staging.vemeet.online:5349?transport=tcp",
    username: "staginguser",
    credential: "stagingpassword",
  },
];

export default function GuestRoom(props) {
  const [searchParams] = useSearchParams();

  const roomId =
    props.roomId ||
    searchParams.get("roomId") ||
    sessionStorage.getItem("roomId");
  const guestName =
    props.name ||
    searchParams.get("name") ||
    sessionStorage.getItem("guestName") ||
    "Guest";
  const plan =
    props.plan ||
    searchParams.get("plan") ||
    sessionStorage.getItem("hostPlan") ||
    "free";

  // Refs & state
  const localVideoRef = useRef(null);
  const peersRef = useRef({}); // map peerId -> Peer instance
  const peerVideoRefs = useRef({}); // map peerId -> video DOM node (for fullscreen)
  const namesRef = useRef({}); // map socketId -> displayName
  const [peers, setPeers] = useState([]); // [{ id, name, stream }]
  const streamRef = useRef(null);

  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);

  // Chat & participants
  const [participants, setParticipants] = useState([]); // [{id, name}]
  const [messages, setMessages] = useState([]); // {sender, text, time}
  const chatEndRef = useRef(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);

  // Timer (free 75 minutes default)
  const defaultFreeSeconds = 75 * 60;
  const [timeLeft, setTimeLeft] = useState(() =>
    plan === "paid" ? Infinity : defaultFreeSeconds
  );

  // --- helper functions to manage lists safely
  const addParticipant = useCallback((p) => {
    if (!p || !p.id) return;
    setParticipants((prev) => {
      if (prev.find((x) => x.id === p.id)) return prev;
      return [...prev, p];
    });
  }, []);

  const removeParticipant = useCallback((id) => {
    setParticipants((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const addPeerEntry = useCallback((entry) => {
    if (!entry || !entry.id) return;
    setPeers((prev) => {
      if (prev.find((x) => x.id === entry.id)) return prev;
      return [...prev, entry];
    });
  }, []);

  const removePeerEntry = useCallback((id) => {
    setPeers((prev) => prev.filter((p) => p.id !== id));
    // cleanup any stored video ref
    if (peerVideoRefs.current[id]) {
      try {
        delete peerVideoRefs.current[id];
      } catch (e) {}
    }
  }, []);

  const pushMessage = useCallback((msg) => {
    if (!msg) return;
    setMessages((prev) => [...prev, msg]);
  }, []);

  // Scroll chat into view on new messages
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages, chatOpen]);

  // Timer effect for free plan
  useEffect(() => {
    if (plan === "paid") return;
    let t = null;
    t = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev === Infinity) return Infinity;
        if (prev <= 1) {
          clearInterval(t);
          try {
            alert("Meeting time is over. Leaving now.");
          } catch (e) {}
          leaveRoom();
          return 0;
        }
        if (prev === 5 * 60 + 1) {
          try {
            alert("Meeting will end in 5 minutes.");
          } catch (e) {}
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(t);
  }, [plan]);

  // Utility: create Peer with ICE config and handlers (returns simple-peer instance)
  function createPeer({ initiator, otherId, localStream }) {
    if (!otherId) {
      console.warn("createPeer called without otherId");
      return null;
    }
    const peer = new Peer({
      initiator,
      trickle: false,
      stream: localStream,
      config: { iceServers: DEFAULT_ICE_SERVERS },
    });

    peer.on("signal", (signalData) => {
      try {
        signaling.sendSignal(otherId, socket.id, signalData);
      } catch (e) {
        console.warn("[guest signal send failed]", e);
      }
    });

    // remote stream is received
    peer.on("stream", (remoteStream) => {
      const displayName = namesRef.current[otherId] || "Peer";
      try {
        const already = peers.find((p) => p.id === otherId);
        if (already && already.stream && remoteStream && already.stream.id === remoteStream.id) {
          return; // already attached
        }
      } catch (e) {}
      addPeerEntry({ id: otherId, name: displayName, stream: remoteStream });
      addParticipant({ id: otherId, name: displayName });
    });

    peer.on("error", (err) => {
      console.error("[peer error]", otherId, err);
    });

    peer.on("close", () => {
      if (peersRef.current[otherId]) {
        try {
          peersRef.current[otherId].destroy();
        } catch (e) {}
        delete peersRef.current[otherId];
      }
      removePeerEntry(otherId);
      removeParticipant(otherId);
    });

    peer.on("connect", () => {
      // optional debug
    });

    // attach RTCPeerConnection debug logs if available (safe)
    setTimeout(() => {
      try {
        if (peer._pc) {
          peer._pc.oniceconnectionstatechange = () =>
            console.log(`[ICE] ${otherId}:`, peer._pc.iceConnectionState);
          peer._pc.onconnectionstatechange = () =>
            console.log(`[Conn] ${otherId}:`, peer._pc.connectionState || peer._pc.iceConnectionState);
        }
      } catch (e) {}
    }, 0);

    return peer;
  }

  // MAIN effect: connect socket, get media, setup peers/signaling
  useEffect(() => {
    if (!roomId || !guestName) {
      try {
        alert("Missing info. Redirecting to guest access.");
      } catch (e) {}
      window.location.href = "/guestaccess";
      return;
    }

    try {
      socket.connect();
    } catch (e) {
      // no-op if already connected
    }

    // request camera/mic
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then((stream) => {
        streamRef.current = stream;
        // show local preview to guest (muted so they don't hear echo)
        if (localVideoRef.current) {
          try {
            localVideoRef.current.srcObject = stream;
          } catch (e) {}
        }

        addParticipant({ id: socket.id || "local", name: guestName });

        // join room via signaling manager
        signaling.join(roomId, guestName);

        // server sends list of existing peers in the room
        socket.on("all-peers", (others) => {
          if (!Array.isArray(others)) return;
          console.log("[socket] all-peers (guest):", others);
          others.forEach(item => {
            const otherId = (typeof item === "string") ? item : (item && item.id);
            const name = (item && typeof item === "object" && item.name) ? item.name : undefined;
            if (!otherId || otherId === socket.id) return;
            if (name) namesRef.current[otherId] = name;
            if (peersRef.current[otherId]) return;
            const p = createPeer({ initiator: true, otherId, localStream: streamRef.current });
            peersRef.current[otherId] = p;
          });
        });

        socket.on("peer-joined", ({ id, name }) => {
          if (!id || id === socket.id) return;
          namesRef.current[id] = name || "Peer";
          addParticipant({ id, name: name || "Peer" });
          if (peersRef.current[id]) return;
          const p = createPeer({ initiator: false, otherId: id, localStream: streamRef.current });
          peersRef.current[id] = p;
        });

        // receive incoming signal messages
        socket.on("signal", ({ from, data }) => {
          const existing = peersRef.current[from];
          if (existing) {
            try {
              existing.signal(data);
            } catch (err) {
              console.error("[guest signal existing.signal error]", err);
            }
            return;
          }

          // If no peer instance exists for this 'from' yet, create non-initiator
          const p2 = createPeer({ initiator: false, otherId: from, localStream: streamRef.current });
          if (p2) {
            peersRef.current[from] = p2;
            try {
              p2.signal(data);
            } catch (e) {
              console.warn("[guest p2.signal failed]", e);
            }
          }
        });

        // peer left
        socket.on("peer-left", ({ id }) => {
          if (!id) return;
          if (peersRef.current[id]) {
            try {
              peersRef.current[id].destroy();
            } catch (e) {}
            delete peersRef.current[id];
          }
          removePeerEntry(id);
          removeParticipant(id);
        });

        // chat messages distributed by server
        socket.on("chat-message", (msg) => {
          if (msg) pushMessage(msg);
        });
      })
      .catch((err) => {
        console.error("getUserMedia error:", err);
        try {
          alert("Please allow camera and microphone access.");
        } catch (e) {}
      });

    // cleanup on unmount
    return () => {
      try {
        signaling.leave(roomId);
      } catch (e) {}
      try {
        socket.disconnect();
      } catch (e) {}
      Object.keys(peersRef.current).forEach((k) => {
        try {
          peersRef.current[k].destroy();
        } catch (e) {}
        delete peersRef.current[k];
      });
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach((t) => t.stop());
        } catch (e) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CONTROL HANDLERS
  function toggleMute() {
    if (!streamRef.current) return;
    try {
      streamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = muted;
      });
    } catch (e) {}
    setMuted((prev) => !prev);
  }

  function toggleVideo() {
    if (!streamRef.current) return;
    try {
      streamRef.current.getVideoTracks().forEach((track) => {
        track.enabled = videoOff;
      });
    } catch (e) {}
    setVideoOff((prev) => !prev);
  }

  // Chat send
  function handleSendMessage(e) {
    e.preventDefault();
    const text = (e.target.elements.msg.value || "").trim();
    if (!text) return;
    const msg = { sender: guestName, text, time: new Date().toLocaleTimeString() };
    socket.emit("chat-message", msg);
    pushMessage(msg);
    e.target.reset();
  }

  // Fullscreen toggle for a peer video (e.g., host)
  const toggleFullscreenForPeer = async (peerId) => {
    try {
      const vid = peerVideoRefs.current[peerId];
      if (!vid) return;
      const doc = window.document;
      if (
        doc.fullscreenElement === vid ||
        doc.webkitFullscreenElement === vid ||
        doc.mozFullScreenElement === vid ||
        doc.msFullscreenElement === vid
      ) {
        if (doc.exitFullscreen) await doc.exitFullscreen();
        else if (doc.webkitExitFullscreen) await doc.webkitExitFullscreen();
      } else {
        if (vid.requestFullscreen) await vid.requestFullscreen();
        else if (vid.webkitRequestFullscreen) await vid.webkitRequestFullscreen();
        else if (vid.mozRequestFullScreen) await vid.mozRequestFullScreen();
        else if (vid.msRequestFullscreen) await vid.msRequestFullscreen();
      }
    } catch (e) {
      console.warn("Fullscreen toggling error:", e);
    }
  };

  // Leave room / cleanup
  function leaveRoom() {
    try {
      signaling.leave(roomId);
    } catch (e) {}
    try {
      socket.disconnect();
    } catch (e) {}
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((t) => t.stop());
      } catch (e) {}
    }
    Object.keys(peersRef.current).forEach((k) => {
      try {
        peersRef.current[k].destroy();
      } catch (e) {}
    });
    window.location.href = "/";
  }

  // Format time left into mm:ss
  function formatTimeLeft(sec) {
    if (sec === Infinity) return "∞";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? "0" + s : s}`;
  }

  // Render
  return (
    <div className="guestroom-root">
      <header className="room-header">
        <div className="header-item"><strong>Room:</strong> <span>{roomId}</span></div>
        <div className="header-item"><strong>Guest:</strong> <span>{guestName}</span></div>
        <div className="header-item"><strong>Plan:</strong> <span>{plan}</span></div>
        <div className="header-item"><strong>Participants:</strong> <span>{participants.length}</span></div>
        <div className="header-item"><strong>Time Left:</strong> <span>{formatTimeLeft(timeLeft)}</span></div>
      </header>

      <main className="room-main">
        <section className="video-area">
          <div className="local-video">
            <video ref={localVideoRef} autoPlay muted playsInline className="local-preview" />
            <div className="local-label">{guestName} (You)</div>
          </div>

          <div className="peers-grid">
            {peers.map((p) => (
              <div className="peer-box" key={p.id} data-peerid={p.id}>
                <video
                  id={`video-${p.id}`}
                  ref={(el) => {
                    if (!el) return;
                    // store video node by peer id for fullscreen toggling
                    peerVideoRefs.current[p.id] = el;
                    // attach stream only if it's not already attached (helps prevent flicker)
                    try {
                      if (p.stream && (!el.srcObject || el.srcObject.id !== p.stream.id)) {
                        el.srcObject = p.stream;
                      }
                    } catch (e) {}
                  }}
                  autoPlay
                  playsInline
                  className="peer-video"
                />
                <div className="peer-label-row">
                  <div className="peer-label">{p.name || "Peer"}</div>
                  <div className="peer-controls">
                    <button
                      className="peer-fullscreen-btn"
                      title="Toggle Fullscreen"
                      onClick={() => toggleFullscreenForPeer(p.id)}
                    >
                      ⤢
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <aside className={`right-sidebar ${chatOpen || participantsOpen ? "open" : ""}`}>
          <div className="sidebar-tabs">
            <button
              className={`tab-btn ${chatOpen ? "active" : ""}`}
              onClick={() => { setChatOpen(true); setParticipantsOpen(false); }}
            >
              Chat
            </button>
            <button
              className={`tab-btn ${participantsOpen ? "active" : ""}`}
              onClick={() => { setParticipantsOpen(true); setChatOpen(false); }}
            >
              Participants
            </button>
          </div>

          <div className="sidebar-content">
            {chatOpen && (
              <div className="chat-container">
                <div className="chat-messages">
                  {messages.map((m, i) => (
                    <div key={i} className="chat-message">
                      <div className="chat-sender">{m.sender}</div>
                      <div className="chat-text">{m.text}</div>
                      <div className="chat-time">{m.time}</div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <form className="chat-form" onSubmit={handleSendMessage}>
                  <input name="msg" placeholder="Type a message and press Enter" autoComplete="off" />
                </form>
              </div>
            )}

            {participantsOpen && (
              <div className="participants-panel">
                <h4>Participants ({participants.length})</h4>
                <ul className="participants-list">
                  {participants.map((p) => (
                    <li key={p.id}>{p.name || "Unknown"}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </aside>
      </main>

      <footer className="control-panel">
        <div className="controls-left">
          <button onClick={toggleMute}>{muted ? "Unmute" : "Mute"}</button>
          <button onClick={toggleVideo}>{videoOff ? "Video On" : "Video Off"}</button>
        </div>

        <div className="controls-center">
          <button onClick={() => { setChatOpen(!chatOpen); setParticipantsOpen(false); }}>
            {chatOpen ? "Close Chat" : "Open Chat"}
          </button>
          <button onClick={() => { setParticipantsOpen(!participantsOpen); setChatOpen(false); }}>
            {participantsOpen ? "Close Participants" : "Open Participants"}
          </button>
        </div>

        <div className="controls-right">
          <button className="leave-btn" onClick={leaveRoom}>Leave</button>
        </div>
      </footer>
    </div>
  );
}
